// Shared helpers for rendering subscription-plan cards.
//
// Feature keys stored on a plan are technical gating keys ("setup_agent",
// "organization.manage"). This maps them to the same human labels the
// master-admin feature catalog uses, and derives the "<N> Chatbots"
// allowance line from the plan's botLimit field so every dynamically
// created plan shows it without needing a hand-typed feature string.

// Mirrors the backend feature catalog labels (AdminController /plans/features).
const FEATURE_LABELS: Record<string, string> = {
  // Main
  dashboard: "Dashboard",
  chatbots: "Chatbots",
  conversations: "Conversations",
  // Tools
  setup_agent: "Setup Agent",
  aiproviders: "AI Model Integration",
  compliance: "Compliance Center",
  apidocs: "API Docs",
  settings: "Settings",
  custom_branding: "Custom Branding",
  "tools.custom": "Custom Tools",
  "tools.ai_builder": "AI Tool Builder",
  audit_trail: "Tool Audit",
  // Calls
  calls: "Calls Module",
  "calls.workspace": "Agent Workspace",
  "calls.logs": "Call Logs",
  "calls.voicemail": "Voicemail",
  "calls.callbacks": "Callbacks",
  "calls.supervisor": "Supervisor Live Dashboard",
  "calls.reports": "Reports & Export",
  "calls.settings": "Call Settings",
  "calls.basic": "Basic Calls",
  "calls.queues": "Multiple Queues & Routing",
  "calls.ivr": "IVR Menu Builder",
  "calls.recording": "Call Recording",
  "calls.ai": "AI Summary / Sentiment",
  "calls.conference": "Multi-Party Conference",
  // Tickets
  tickets: "Tickets Module",
  // Live chat
  livechat: "Live Chat Module",
  "livechat.operator": "Operator Dashboard",
  "livechat.offline": "Offline Messages",
  "livechat.settings": "Live Chat Settings",
  // Integrations
  integrations: "Integrations",
  webhooks: "Webhooks",
  // Organization
  "organization.manage": "Organization Manage",
  "organization.history": "Organization History",
  workforce: "Workforce",
  "users.manage": "Users Manage",
  "roles.manage": "Roles Manage",
  sso: "Identity & SSO",
  scim: "SCIM Provisioning",
};

/** Human label for a feature key. Unknown keys are prettified generically. */
export function featureLabel(key: string): string {
  const known = FEATURE_LABELS[key.toLowerCase()];
  if (known) return known;
  // Free-text features (e.g. "Email Support", "1,000 Messages/Month") that
  // already read like labels are kept as-is; raw-looking keys get prettified.
  if (/[A-Z ]/.test(key)) return key;
  return key
    .replace(/[._]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

type PlanLike = {
  botLimit?: number | null;
  features?: string[] | null;
};

/** Matches quota/limit strings that should be hidden from plan cards. */
const QUOTA_RE = [
  // Numeric quantities: "3 Chatbots", "10,000 Messages/Month", "5 Agents", etc.
  /^\s*[\d,]+\s*(chatbots?|bots?|messages?|agents?|users?|seats?)(\s*\/\s*(month|mo|year|yr|day))?\s*$/i,
  // "Unlimited X" phrasing — still a limit statement, not a benefit description.
  /unlimited\s+(chatbots?|bots?|messages?|agents?|users?|seats?)/i,
];

/**
 * Display list for a plan card: each selected feature as a readable label,
 * with all limit/quota strings (bot counts, message quotas, agent/user limits)
 * stripped out so only genuine feature benefits remain.
 */
export function planDisplayFeatures(plan: PlanLike): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const f of plan.features ?? []) {
    if (QUOTA_RE.some((re) => re.test(f))) continue; // skip any quota/limit entry
    const label = featureLabel(f);
    const k = label.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(label);
  }
  return out;
}
